import React, { useState,useContext } from 'react';
import { Container, Row, Col, Card, Badge, Button, Form, InputGroup, Modal, Table } from 'react-bootstrap';
import { MessageSquare, Search, Plus, X, Send, FileText, Calendar,  Mail, Phone } from 'lucide-react';
import '../../../assets/css/donor/dashboard/SupportTicket.css';
import DonorLayout from '../../../layouts/donor/DonorLayout';
import AppContext from "../../../context/AppContext";

const SupportTicket = () => {
  const { baseUrl, regExpChecker } = useContext(AppContext);
  const [title, setTitle] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState("Payment");
  const [priority, setPriority] = useState("Medium");

  const [titleError, setTitleError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  // States
  const [stShowCreateModal, setStShowCreateModal] = useState(false);
  const [stShowDetailsModal, setStShowDetailsModal] = useState(false);
  const [stSearchTerm, setStSearchTerm] = useState('');
  const [stSelectedCategory, setStSelectedCategory] = useState('all');
  const [stSelectedTicket, setStSelectedTicket] = useState(null);
  const [stTickets, setStTickets] = useState([
    {
      id: 'T-12703',
      title: 'Donation Payment Issue',
      description: 'Payment not reflecting in donor account after successful transaction',
      category: 'Payment',
      status: 'In Progress',
      priority: 'High',
      createdAt: '2024-04-18',
      email: 'alihasan@example.com',
      phone: '+92 300 1234567',
      assignedTo: 'Support Team',
      replies: []
    },
    {
      id: 'T-12929',
      title: 'Account Verification',
      description: 'Unable to verify documents for donor profile',
      category: 'Account',
      status: 'Resolved',
      priority: 'Medium',
      createdAt: '2024-04-17',
      email: 'ayaan@example.com',
      phone: '+92 301 9876543',
      assignedTo: 'Admin Team',
      replies: []
    },
    {
      id: 'T-18015',
      title: 'Donation Refund Request',
      description: 'Request for donation refund due to wrong amount',
      category: 'Refund',
      status: 'Pending',
      priority: 'Low',
      createdAt: '2024-04-16',
      email: 'sania@example.com',
      phone: '+92 333 5551234',
      assignedTo: 'Finance Team',
      replies: []
    }
  ]);

  // Create Ticket Form State
  const [stNewTicket, setStNewTicket] = useState({
    title: '',
    description: '',
    category: 'Payment',
    priority: 'Medium',
    email: '',
    phone: ''
  });

  // Reply State
  const [stReplyText, setStReplyText] = useState('');

  // Statistics
  const stStats = {
    total: stTickets.length,
    inProgress: stTickets.filter(t => t.status === 'In Progress').length,
    resolved: stTickets.filter(t => t.status === 'Resolved').length,
    pending: stTickets.filter(t => t.status === 'Pending').length
  };

  // Filter tickets
  const stFilteredTickets = stTickets.filter(ticket => {
    const matchesSearch =
      ticket.id.toLowerCase().includes(stSearchTerm.toLowerCase()) ||
      ticket.title.toLowerCase().includes(stSearchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(stSearchTerm.toLowerCase());

    const matchesCategory =
      stSelectedCategory === 'all' || ticket.category.toLowerCase() === stSelectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Handle Create Ticket
  const handleStCreateTicket = () => {
    if (!stNewTicket.title || !stNewTicket.description || !stNewTicket.email) {
      alert('Please fill all required fields');
      return;
    }

    const newTicket = {
      id: `T-${Math.floor(10000 + Math.random() * 90000)}`,
      ...stNewTicket,
      status: 'Pending',
      createdAt: new Date().toISOString().split('T')[0],
      assignedTo: 'Support Team',
      replies: []
    };

    setStTickets([newTicket, ...stTickets]);
    setStShowCreateModal(false);
    setStNewTicket({
      title: '',
      description: '',
      category: 'Payment',
      priority: 'Medium',
      email: '',
      phone: ''
    });
  };

  // Handle Send Reply
  const handleStSendReply = () => {
    if (!stReplyText.trim()) return;

    const updatedTickets = stTickets.map(ticket => {
      if (ticket.id === stSelectedTicket.id) {
        return {
          ...ticket,
          replies: [
            ...ticket.replies,
            {
              id: Date.now(),
              text: stReplyText,
              sender: 'You',
              timestamp: new Date().toLocaleString()
            }
          ]
        };
      }
      return ticket;
    });

    setStTickets(updatedTickets);
    setStSelectedTicket(updatedTickets.find(t => t.id === stSelectedTicket.id));
    setStReplyText('');
  };

  // Get Status Badge Variant
  const getStStatusBadge = (status) => {
    const variants = {
      'In Progress': 'primary',
      'Resolved': 'success',
      'Pending': 'warning',
      'Closed': 'secondary'
    };
    return variants[status] || 'primary';
  };

  // Get Priority Color
  const getStPriorityColor = (priority) => {
    const colors = {
      'High': '#dc3545',
      'Medium': '#ffc107',
      'Low': '#28a745'
    };
    return colors[priority] || '#6c757d';
  };

  return (
    <DonorLayout>
      <div className="st-ticket-container">
        <Container fluid>

          {/* Header */}
          <Row className="st-header mb-4">
            <Col>
              <div className="st-title-wrapper">
                <h2 className="st-main-title">Help & Support</h2>
                <p className="st-subtitle">Manage your support tickets and get help</p>
              </div>
            </Col>
          </Row>

          {/* Statistics Cards */}
          <Row className="mb-4">
            <Col lg={3} md={6} className="mb-3">
              <Card className="st-stat-card">
                <Card.Body>
                  <div className="st-stat-icon st-stat-total">
                    <MessageSquare size={28} />
                  </div>
                  <h3 className="st-stat-number">{stStats.total}</h3>
                  <p className="st-stat-label">Total Tickets</p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <Card className="st-stat-card">
                <Card.Body>
                  <div className="st-stat-icon st-stat-progress">
                    <FileText size={28} />
                  </div>
                  <h3 className="st-stat-number">{stStats.inProgress}</h3>
                  <p className="st-stat-label">In Progress</p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <Card className="st-stat-card">
                <Card.Body>
                  <div className="st-stat-icon st-stat-resolved">
                    <span className="st-check-icon">✓</span>
                  </div>
                  <h3 className="st-stat-number">{stStats.resolved}</h3>
                  <p className="st-stat-label">Resolved</p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <Card className="st-stat-card">
                <Card.Body>
                  <div className="st-stat-icon st-stat-pending">
                    <Calendar size={28} />
                  </div>
                  <h3 className="st-stat-number">{stStats.pending}</h3>
                  <p className="st-stat-label">Pending</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Search and Create */}
          <Row className="mb-4">
            <Col md={8} className="mb-3 mb-md-0">
              <InputGroup className="st-search-group">
                <InputGroup.Text className="st-search-icon">
                  <Search size={18} />
                </InputGroup.Text>
                <Form.Control
                  className="st-search-input"
                  placeholder="Search tickets by ID, title, or description..."
                  value={stSearchTerm}
                  onChange={(e) => setStSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <Button
                className="st-create-btn"
                onClick={() => setStShowCreateModal(true)}
              >
                <Plus size={18} /> Create New Ticket
              </Button>
            </Col>
          </Row>

          {/* Filter Tabs */}
          <Row className="mb-4">
            <Col>
              <div className="st-filter-tabs">
                {['all', 'payment', 'account', 'refund'].map(cat => (
                  <button
                    key={cat}
                    className={`st-filter-tab ${stSelectedCategory === cat ? 'active' : ''}`}
                    onClick={() => setStSelectedCategory(cat)}
                  >
                    {cat === 'all' ? 'All Tickets' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
            </Col>
          </Row>

          {/* Tickets Table */}
          <Row>
            <Col>
              <Card className="st-table-card">
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="st-table mb-0">
                      <thead className="st-table-header">
                        <tr>
                          <th>Ticket ID</th>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Created Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stFilteredTickets.length > 0 ? (
                          stFilteredTickets.map(ticket => (
                            <tr key={ticket.id} className="st-table-row">
                              <td className="st-ticket-id">{ticket.id}</td>
                              <td>
                                <div className="st-ticket-title">{ticket.title}</div>
                                <div className="st-ticket-description">
                                  {ticket.description.substring(0, 50)}...
                                </div>
                              </td>
                              <td>
                                <Badge bg="secondary" className="st-category-badge">
                                  {ticket.category}
                                </Badge>
                              </td>
                              <td>
                                <span
                                  className="st-priority-badge"
                                  style={{ backgroundColor: getStPriorityColor(ticket.priority) }}
                                >
                                  {ticket.priority}
                                </span>
                              </td>
                              <td>
                                <Badge bg={getStStatusBadge(ticket.status)} className="st-status-badge">
                                  {ticket.status}
                                </Badge>
                              </td>
                              <td>{ticket.createdAt}</td>
                              <td>
                                <Button
                                  size="sm"
                                  className="st-view-btn"
                                  onClick={() => {
                                    setStSelectedTicket(ticket);
                                    setStShowDetailsModal(true);
                                  }}
                                >
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              <MessageSquare size={48} className="text-muted mb-2" />
                              <p className="text-muted mb-0">No tickets found</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </Container>

        {/* CREATE TICKET MODAL */}
        <Modal
          show={stShowCreateModal}
          onHide={() => setStShowCreateModal(false)}
          size="lg"
          centered
          className="st-modal"
        >
          <Modal.Header className="st-modal-header">
            <Modal.Title>Create New Ticket</Modal.Title>
            <Button
              variant="link"
              className="st-close-btn"
              onClick={() => setStShowCreateModal(false)}
            >
              <X size={24} />
            </Button>
          </Modal.Header>

          <Modal.Body className="st-modal-body">
            <Form>
              <Row>
                <Col md={12} className="mb-3">
                  <Form.Label className="st-form-label">Ticket Title *</Form.Label>
                  <Form.Control
                    className="st-form-input"
                    type="text"
                    placeholder="Enter ticket title"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      setTitleError(regExpChecker("string", e.target.value));
                    }}
                  />
                  <p style={{ color: "red" }}>{titleError}</p>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Label className="st-form-label">Category *</Form.Label>
                  <Form.Select
                    className="st-form-input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >

                    <option value="Payment">Payment</option>
                    <option value="Account">Account</option>
                    <option value="Refund">Refund</option>
                    <option value="Technical">Technical</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Label className="st-form-label">Priority *</Form.Label>
                  <Form.Select
                    className="st-form-input"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </Form.Select>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Label className="st-form-label">Email *</Form.Label>
                  <Form.Control
                    className="st-form-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError(regExpChecker("email", e.target.value));
                    }}
                  />
                  <p style={{ color: "red" }}>{emailError}</p>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Label className="st-form-label">Phone</Form.Label>
                  <Form.Control
                    className="st-form-input"
                    type="tel"
                    placeholder="+92 300 1234567"
                    value={phone}
                    onChange={(e) => {

                      setPhone(e.target.value);
                      setPhoneError(regExpChecker("phone", e.target.value));
                    }}
                  />
                  <p style={{ color: "red" }}>{phoneError}</p>
                </Col>

                <Col md={12} className="mb-3">
                  <Form.Label className="st-form-label">Description *</Form.Label>
                  <Form.Control
                    className="st-form-textarea"
                    as="textarea"
                    rows={4}
                    placeholder="Describe your issue in detail..."
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setDescriptionError(regExpChecker("string", e.target.value));
                    }}
                  />
                  <p style={{ color: "red" }}>{descriptionError}</p>
                </Col>
              </Row>
            </Form>
          </Modal.Body>

          <Modal.Footer className="st-modal-footer">
            <Button
              variant="secondary"
              className="st-cancel-btn"
              onClick={() => setStShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="st-submit-btn"
              onClick={handleStCreateTicket}
            >
              <Plus size={18} /> Create Ticket
            </Button>
          </Modal.Footer>
        </Modal>

        {/* TICKET DETAILS MODAL */}
        <Modal
          show={stShowDetailsModal}
          onHide={() => setStShowDetailsModal(false)}
          size="lg"
          centered
          className="st-modal"
        >
          <Modal.Header className="st-modal-header">
            <Modal.Title>Ticket Details</Modal.Title>
            <Button
              variant="link"
              className="st-close-btn"
              onClick={() => setStShowDetailsModal(false)}
            >
              <X size={24} />
            </Button>
          </Modal.Header>

          <Modal.Body className="st-modal-body">
            {stSelectedTicket && (
              <>
                {/* Ticket Info */}
                <Card className="st-detail-card mb-3">
                  <Card.Body>
                    <Row>
                      <Col md={6} className="mb-3">
                        <div className="st-detail-item">
                          <label><MessageSquare size={16} /> Ticket ID</label>
                          <span>{stSelectedTicket.id}</span>
                        </div>
                      </Col>
                      <Col md={6} className="mb-3">
                        <div className="st-detail-item">
                          <label><Calendar size={16} /> Created Date</label>
                          <span>{stSelectedTicket.createdAt}</span>
                        </div>
                      </Col>
                      <Col md={6} className="mb-3">
                        <div className="st-detail-item">
                          <label><Mail size={16} /> Email</label>
                          <span>{stSelectedTicket.email}</span>
                        </div>
                      </Col>
                      <Col md={6} className="mb-3">
                        <div className="st-detail-item">
                          <label><Phone size={16} /> Phone</label>
                          <span>{stSelectedTicket.phone}</span>
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="st-detail-item">
                          <label>Status</label>
                          <Badge bg={getStStatusBadge(stSelectedTicket.status)}>
                            {stSelectedTicket.status}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Description */}
                <Card className="st-detail-card mb-3">
                  <Card.Body>
                    <h6 className="st-section-title">Description</h6>
                    <p className="st-description-text">{stSelectedTicket.description}</p>
                  </Card.Body>
                </Card>

                {/* Replies */}
                <Card className="st-detail-card mb-3">
                  <Card.Body>
                    <h6 className="st-section-title">Replies</h6>
                    {stSelectedTicket.replies.length > 0 ? (
                      stSelectedTicket.replies.map(reply => (
                        <div key={reply.id} className="st-reply-item">
                          <div className="st-reply-header">
                            <strong>{reply.sender}</strong>
                            <span className="st-reply-time">{reply.timestamp}</span>
                          </div>
                          <p className="st-reply-text">{reply.text}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted">No replies yet</p>
                    )}
                  </Card.Body>
                </Card>

                {/* Reply Form */}
                <Card className="st-detail-card">
                  <Card.Body>
                    <h6 className="st-section-title">Add Reply</h6>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      className="st-form-textarea mb-3"
                      placeholder="Type your reply here..."
                      value={stReplyText}
                      onChange={(e) => setStReplyText(e.target.value)}
                    />
                    <Button
                      className="st-send-btn"
                      onClick={handleStSendReply}
                    >
                      <Send size={18} /> Send Reply
                    </Button>
                  </Card.Body>
                </Card>
              </>
            )}
          </Modal.Body>

          <Modal.Footer className="st-modal-footer">
            <Button
              variant="secondary"
              className="st-cancel-btn"
              onClick={() => setStShowDetailsModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

      </div>
    </DonorLayout>
  );
};

export default SupportTicket;